import { useCallback, useEffect, useRef, useState } from 'react';
import { cancelBillingAtPeriodEnd, getBilling, getBillingPlans, openBillingPortal, resumeBilling, startBillingCheckout, syncBilling } from '../api/billing';
import { ApiError, COMMERCIAL_STATUS_EVENT, getErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { canManageBilling } from '../auth/permissions';
import { Badge, Button, Card, Dialog, EmptyPanel, ErrorPanel, LoadingPanel } from '../components/ui/primitives';
import type { BillingAction, BillingOverview, BillingPlan } from '../types/billing';
import {
  BILLING_TITLE,
  actionLabel,
  billingReturnStep,
  cancelConfirmText,
  cancelDoneText,
  hasConfirmedPrice,
  limitText,
  priceText,
  statusLabel,
  statusSummary,
  statusTone,
} from '../utils/billingView';
import { redirectTo } from '../utils/externalRedirect';
import { DEFAULT_STORE_TIMEZONE } from '../utils/timezones';

type Message = { tone: 'info' | 'success' | 'danger'; text: string };

/**
 * ONB-007 (Macrofase B) — «Plan y facturación».
 *
 * Todo lo que se ve lo decide el servidor: el estado, las fechas (con su hora, nunca la del navegador) y
 * qué acciones hay. Elegir un plan lleva a una página SEGURA del servicio de pagos para guardar la
 * tarjeta (Negocio360 nunca ve sus datos); al volver, se le pregunta al servidor qué pasó: el regreso no
 * prueba nada por sí mismo. El gerente solo consulta; el dueño administra.
 */
export function BillingPage() {
  const { user, stores } = useAuth();
  const owner = canManageBilling(user?.role);
  const timeZone = stores[0]?.timezone ?? DEFAULT_STORE_TIMEZONE;
  const [view, setView] = useState<BillingOverview | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [message, setMessage] = useState<Message | null>(null);
  const [plans, setPlans] = useState<BillingPlan[] | null>(null);
  const [pricesUnavailable, setPricesUnavailable] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const returnStep = useRef(billingReturnStep(window.location.hash));

  const apply = useCallback((next: BillingOverview) => {
    setView(next);
    // El aviso global y el menú vuelven a leer el estado (pudo cambiar aquí).
    window.dispatchEvent(new CustomEvent(COMMERCIAL_STATUS_EVENT));
  }, []);

  const load = useCallback(async () => {
    setLoadError(null);
    const step = returnStep.current;
    returnStep.current = null;
    try {
      if (step === 'checkout-canceled') setMessage({ tone: 'info', text: 'No se guardó ninguna forma de pago. Puedes intentarlo cuando quieras.' });
      if ((step === 'checkout-return' || step === 'portal-return') && owner) {
        const synced = await syncBilling();
        apply(synced);
        if (step === 'checkout-return') {
          setMessage(synced.checkoutPending
            ? { tone: 'info', text: 'Estamos confirmando tu forma de pago con el servicio de pagos. Puede tardar unos segundos.' }
            : synced.checkoutFailed
              ? { tone: 'danger', text: 'No pudimos activar tu plan con esa forma de pago. Puedes intentarlo de nuevo.' }
              : synced.subscription ? { tone: 'success', text: 'Listo: tu forma de pago quedó guardada.' } : null);
        }
      } else {
        setView(await getBilling());
      }
      if (step) window.history.replaceState(null, '', '#billing');
    } catch (error) {
      if (error instanceof ApiError && error.payload?.error === 'SAAS_BILLING_DISABLED') setDisabled(true);
      else setLoadError(getErrorMessage(error, 'No pudimos cargar tu plan.'));
    }
  }, [apply, owner]);

  useEffect(() => { void load(); }, [load]);

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    setMessage(null);
    try {
      await action();
    } catch (error) {
      setMessage({ tone: 'danger', text: getErrorMessage(error, 'No se pudo completar. Intenta de nuevo.') });
    } finally {
      setBusy(null);
    }
  };

  const onAction = (action: BillingAction) => {
    switch (action) {
      case 'CHOOSE_PLAN':
        void run(action, async () => {
          const result = await getBillingPlans();
          setPlans(result.plans);
          setPricesUnavailable(result.pricesUnavailable === true);
        });
        break;
      case 'UPDATE_PAYMENT_METHOD':
        void run(action, async () => redirectTo((await openBillingPortal('PAYMENT_METHOD_UPDATE')).url));
        break;
      case 'MANAGE_SUBSCRIPTION':
        void run(action, async () => redirectTo((await openBillingPortal('DEFAULT')).url));
        break;
      case 'CANCEL_AT_PERIOD_END':
        setConfirmCancel(true);
        break;
      case 'RESUME_SUBSCRIPTION':
        void run(action, async () => {
          apply(await resumeBilling());
          setMessage({ tone: 'success', text: 'Listo: tu plan sigue activo.' });
        });
        break;
    }
  };

  const checkout = (planCode: string) => run(`checkout:${planCode}`, async () => redirectTo((await startBillingCheckout(planCode)).url));

  const cancel = () => run('cancel', async () => {
    const next = await cancelBillingAtPeriodEnd();
    apply(next);
    setConfirmCancel(false);
    setMessage({ tone: 'info', text: cancelDoneText(next, timeZone) });
  });

  const refresh = () => run('refresh', async () => apply(owner ? await syncBilling() : await getBilling()));

  const status = view?.status ?? 'TRIALING';
  const inTrial = status === 'TRIALING';

  return (
    <section aria-labelledby="billing-title" className="n3-billing">
      <header className="n3-billing__header">
        <h1 className="n3-billing__title" id="billing-title">{BILLING_TITLE}</h1>
        <p className="n3-billing__lead">Tu plan de Negocio360 y tu forma de pago. Tu información es tuya y sigue aquí pase lo que pase con tu plan.</p>
      </header>

      {message && (
        <p
          className={message.tone === 'danger' ? 'n3-billing__message n3-billing__message--danger' : message.tone === 'success' ? 'n3-billing__message n3-billing__message--success' : 'n3-billing__message'}
          role={message.tone === 'danger' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      )}

      {disabled && <EmptyPanel description="Tu negocio sigue funcionando como siempre." icon="card" title="La facturación todavía no está disponible" />}
      {!disabled && !view && !loadError && <LoadingPanel label="Cargando tu plan…" />}
      {loadError && <ErrorPanel description={loadError} onRetry={() => void load()} title="No pudimos cargar tu plan" />}

      {view && (
        <Card aria-labelledby="billing-status-title" as="section" className="n3-billing__card">
          <div className="n3-billing__head">
            <h2 className="n3-billing__heading" id="billing-status-title">Tu plan</h2>
            <span className="n3-billing__badges">
              <Badge dot tone={statusTone(status)}>{statusLabel(status)}</Badge>
              {view.testMode && view.billingMode === 'SELF_SERVE' && <Badge tone="info">Modo de prueba</Badge>}
            </span>
          </div>
          <p className="n3-billing__plan">{view.subscription?.plan?.name ?? view.plan.name}</p>
          <div className="n3-billing__summary">
            {statusSummary(view, timeZone).map((line) => <p key={line}>{line}</p>)}
          </div>
          {owner && view.actions.length > 0 && (
            <div className="n3-billing__actions">
              {view.actions.map((action, index) => (
                <Button
                  key={action}
                  loading={busy === action}
                  loadingLabel="Un momento…"
                  onClick={() => onAction(action)}
                  variant={action === 'CANCEL_AT_PERIOD_END' ? 'ghost' : index === 0 ? 'primary' : 'secondary'}
                >
                  {actionLabel(action, status)}
                </Button>
              ))}
            </div>
          )}
          {owner && view.pendingCheckout && (
            <div className="n3-billing__actions">
              <Button loading={busy === 'refresh'} loadingLabel="Consultando…" onClick={() => void refresh()} variant="secondary">Actualizar</Button>
            </div>
          )}
          {!owner && <p className="n3-billing__note">Solo la persona dueña del negocio puede elegir el plan o cambiar la forma de pago.</p>}
        </Card>
      )}

      {owner && plans && (
        <section aria-labelledby="billing-plans-title" className="n3-billing__plans">
          <h2 className="n3-billing__heading" id="billing-plans-title">Elige tu plan</h2>
          <p className="n3-billing__note">
            {inTrial
              ? 'Conservas todos los días de tu prueba gratis: el primer cobro es el día que termina.'
              : 'El cobro se hace al confirmar tu forma de pago.'}
            {' '}Vas a una página segura del servicio de pagos; Negocio360 nunca ve los datos de tu tarjeta.
          </p>
          {plans.length === 0 && (
            <EmptyPanel
              plain
              title={pricesUnavailable ? 'No pudimos confirmar los precios con el servicio de pagos.' : 'No hay planes disponibles por ahora.'}
              {...(pricesUnavailable ? { description: 'Intenta de nuevo en unos minutos.' } : {})}
            />
          )}
          {plans.length > 0 && pricesUnavailable && (
            <p className="n3-billing__note" role="status">Algunos planes no aparecen porque no pudimos confirmar su precio. Intenta de nuevo en unos minutos.</p>
          )}
          {plans.length > 0 && (
            <ul className="n3-billing__plan-list">
              {plans.map((plan) => (
                <Card as="li" className="n3-billing__plan-card" key={plan.code} radius="lg">
                  <h3 className="n3-billing__plan-name">{plan.name}</h3>
                  {plan.description && <p className="n3-billing__note">{plan.description}</p>}
                  <p className="n3-billing__price">{priceText(plan.price)}</p>
                  <ul className="n3-billing__limits">
                    <li>{limitText('Tiendas', plan.limits.stores)}</li>
                    <li>{limitText('Usuarios', plan.limits.users)}</li>
                  </ul>
                  <Button
                    disabled={!hasConfirmedPrice(plan.price)}
                    fullWidth
                    loading={busy === `checkout:${plan.code}`}
                    loadingLabel="Abriendo la página de pago…"
                    onClick={() => void checkout(plan.code)}
                    variant="primary"
                  >
                    {inTrial ? 'Agregar método de pago' : 'Continuar al pago'}
                  </Button>
                </Card>
              ))}
            </ul>
          )}
        </section>
      )}

      {confirmCancel && view && (
        <Dialog
          footer={(
            <>
              <Button disabled={busy === 'cancel'} onClick={() => setConfirmCancel(false)} variant="secondary">Mantener mi plan</Button>
              <Button loading={busy === 'cancel'} loadingLabel="Programando…" onClick={() => void cancel()} variant="primary">Cancelar al final del periodo</Button>
            </>
          )}
          isBusy={busy === 'cancel'}
          onClose={() => setConfirmCancel(false)}
          title="¿Cancelar tu plan al final del periodo?"
        >
          <p className="n3-billing__note">{cancelConfirmText(view, timeZone)}</p>
        </Dialog>
      )}
    </section>
  );
}
